import { AsyncPipe, NgClass, NgFor, NgIf, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import {
  MatLegacyOptionModule,
  MatLegacyOptionSelectionChange as MatOptionSelectionChange,
} from '@angular/material/legacy-core';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { ErrorDirective, ErrorsDirective } from '@shared/directives/errors';
import { TokeniserIFrameMessage } from '@shared/models/tokeniser-iframe-message';
import { SafeUrlPipe } from '@shared/pipes/safe-url.pipe';
import { CardPaymentTypesService } from '@shared/services/card-payment-types';
import { TokenizerErrorListenerDirective } from '@shared/shared/directives/tokenizer-error-listener.directive';
import { TokenizerMessageListenerDirective } from '@shared/shared/directives/tokenizer-message-listener.directive';
import { TokeniserSite } from '@shared/shared/enums/tokeniser-site.enum';
import { ICountry } from '@shared/shared/interfaces/country.interfaces';
import { IMemberProfilePaymentCard } from '@shared/shared/interfaces/member-profile-payment-card.interfaces';
import { IPaymentCardType, IframeFormValues } from '@shared/shared/interfaces/payment-card-types.interface';
import { CountryService } from '@shared/shared/services/country.service';
import { TokeniserService, TokeniserURL } from '@shared/shared/services/tokeniser.service';
import * as MemberProfileStore from '@shared/store/member-profile';
import { MemberProfilePaymentCardAction, getMemberProfilePaymentCardsFeatureState } from '@shared/store/member-profile';
import { Angulartics2Segment } from 'angulartics2';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, startWith, take } from 'rxjs/operators';

@Component({
  selector: 'ten-payment-card-form',
  templateUrl: './payment-card-form.component.html',
  styleUrls: ['../popup-form-base.scss', './payment-card-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyOptionModule,
    NgFor,
    AsyncPipe,
    ErrorDirective,
    ErrorsDirective,
    MatLegacyInputModule,
    SlicePipe,
    TokenizerErrorListenerDirective,
    TokenizerMessageListenerDirective,
    MatLegacyAutocompleteModule,
    MatLegacyCheckboxModule,
    MatLegacyButtonModule,
    NgClass,
    MatLegacyProgressSpinnerModule,
    SafeUrlPipe,
    NgIf,
  ],
})
export class PaymentCardFormComponent implements OnInit, OnDestroy {
  @Input() public pastDataId: number;

  @Output() public closed = new EventEmitter<void>();

  public noSpecialCharsValidator = Validators.pattern(/^[^;<>()*]+$/i);

  public newPaymentCard: boolean;
  public paymentCardForm: UntypedFormGroup;
  public tokeniserURL: TokeniserURL;
  public paymentCardToEdit: IMemberProfilePaymentCard;
  public paymentCards$: Observable<IMemberProfilePaymentCard[]>;
  public paymentCardTypes$: Observable<IPaymentCardType[]>;
  public currentCardType: IPaymentCardType;
  public countries$: Observable<ICountry[]>;
  public filteredCountries$: Observable<ICountry[]>;
  public stateRequired: boolean;

  private tokenizerValid: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    private tokeniserService: TokeniserService,
    private paymentTypeService: CardPaymentTypesService,
    private ngrxStore: Store<MemberProfilePaymentCardAction>,
    private countryService: CountryService,
    private angulartics2segment: Angulartics2Segment
  ) {
    this.paymentCards$ = this.ngrxStore.select(getMemberProfilePaymentCardsFeatureState);
    this.paymentCardTypes$ = this.paymentTypeService.getCardTypes().pipe(shareReplay(1));
    this.countries$ = this.countryService.getCountries();

    this.paymentCardForm = new UntypedFormGroup({
      // Card details
      card_type: new UntypedFormControl('', [Validators.required]),

      // Card details iFrame fields
      card_holder_name: new UntypedFormControl('', [Validators.required]),
      card_number: new UntypedFormControl('', [Validators.required]),
      expiry_date_month: new UntypedFormControl('', [Validators.required]),
      expiry_date_year: new UntypedFormControl('', [Validators.required]),

      // Billing address
      address: new UntypedFormGroup({
        house_number: new UntypedFormControl('', [Validators.maxLength(10), this.noSpecialCharsValidator]),
        street1: new UntypedFormControl('', [Validators.required, this.noSpecialCharsValidator]),
        street2: new UntypedFormControl('', [this.noSpecialCharsValidator]),
        city: new UntypedFormControl('', [Validators.required, this.noSpecialCharsValidator]),
        state: new UntypedFormControl('', [this.noSpecialCharsValidator]),
        country: new UntypedFormControl('', [Validators.required]),
        post_code: new UntypedFormControl('', [this.noSpecialCharsValidator]),
      }),

      // Flags
      is_default_card: new UntypedFormControl(false),
      offline_use: new UntypedFormControl(false),
    });
  }

  public ngOnInit() {
    this.newPaymentCard = this.pastDataId === undefined;

    this.filteredCountries$ = combineLatest([
      this.countries$,
      this.paymentCardForm.get('address.country').valueChanges.pipe<string, string>(
        map((value) => value.toLowerCase().trim()),
        startWith('')
      ),
    ]).pipe(
      map(([countries, value]) =>
        value ? countries.filter((option) => option.name.toLowerCase().includes(value)) : countries
      )
    );

    combineLatest([this.paymentCardTypes$, this.paymentCards$])
      .pipe(
        filter(([cardTypes, paymentCards]: [Array<IPaymentCardType>, Array<IMemberProfilePaymentCard>]) => {
          const hasCardTypes = cardTypes !== null && cardTypes !== undefined;
          const hasPaymentCards = paymentCards !== null && paymentCards !== undefined;
          return !!hasCardTypes && !!hasPaymentCards;
        }),
        take(1)
      )
      .subscribe(([cardTypes, paymentCards]: [Array<IPaymentCardType>, Array<IMemberProfilePaymentCard>]) => {
        if (!this.newPaymentCard) {
          this.paymentCardToEdit = paymentCards?.find((paymentCard) => paymentCard.id === this.pastDataId);
          this.currentCardType = cardTypes.find((cardType) => cardType.name === this.paymentCardToEdit?.card_type);
          this.initEditForm(this.paymentCardToEdit);
        }

        const tokeniserSite = this.newPaymentCard ? TokeniserSite.PlatformAddCard : TokeniserSite.PlatformEditCard;
        const isFirstCardCreation = this.newPaymentCard && !paymentCards?.length;
        const isPreferredCardEdit = this.paymentCardToEdit?.is_default_card;
        const formValues = this.getIframeFormValues(this.paymentCardToEdit);
        // No need to de-tokenise card number because we only show the last 4 characters and is is not editable
        delete formValues.cardNumberToken;

        this.tokeniserURL = this.tokeniserService.getTokeniserURL(tokeniserSite, this.currentCardType, formValues);

        // First card created must be a preferred card
        // Do not allow setting an existing card's preferred flag to false
        if (isFirstCardCreation || isPreferredCardEdit) {
          const preferredControl = this.paymentCardForm.controls.is_default_card;

          preferredControl.setValue(true);
          preferredControl.disable();
        }
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onIframeError() {
    this.angulartics2segment.eventTrack('Store Card Tokeniser Fail', {});
  }

  public updateCardType(cardType: IPaymentCardType, { isUserInput }: MatOptionSelectionChange) {
    if (isUserInput) {
      this.paymentCardForm.patchValue({
        card_number: null,
        expiry_date_month: null,
        expiry_date_year: null,
        card_holder_name: null,
      });
      this.tokeniserURL.updateCardType(cardType, this.getIframeFormValues(this.paymentCardForm.value));
      this.currentCardType = cardType;
    }
  }

  public checkCountry(skipCheck: boolean) {
    if (skipCheck) {
      return;
    }

    this.countries$.pipe(take(1)).subscribe((countries) => {
      const countryControl = this.paymentCardForm.get('address.country');
      const countryByName = countries.find((item) => item.name === countryControl.value);

      if (countryByName) {
        this.setStateValidators(countryByName);
      } else {
        const countryByCode = countries.find((item) => item.code === countryControl.value);

        countryControl.patchValue(countryByCode?.name || '');
        this.setStateValidators(countryByCode);
      }
    });
  }

  public submitForm() {
    this.paymentCardForm.markAllAsTouched();
    this.paymentCardForm.updateValueAndValidity();
    // contains all address fields that are required
    // For editing, the card details are already set, so we only need to check the address
    const formValid =
      (this.newPaymentCard && this.paymentCardForm.valid) ||
      (!this.newPaymentCard && this.paymentCardForm.get('address').valid);

    if (this.tokenizerValid && formValid) {
      this.countries$
        .pipe(
          filter((countries: Array<ICountry>) => !!countries),
          take(1)
        )
        .subscribe((countries: Array<ICountry>) => {
          const countryControl = this.paymentCardForm.get('address.country');
          const formValue = this.paymentCardForm.getRawValue();
          const cardData: IMemberProfilePaymentCard = {
            ...formValue,
            address: {
              ...formValue.address,
              country: countries.find((country) => country.name === countryControl.value)?.code,
              state: formValue.address.state || '',
            },
            online_use: true,
          };

          const action = this.newPaymentCard
            ? new MemberProfileStore.CreateProfilePaymentCard(cardData)
            : new MemberProfileStore.UpdateProfilePaymentCard({ data: cardData, id: this.pastDataId });

          this.ngrxStore.dispatch(action);
          this.closeForm();
        });
    } else {
      const tokeniserSite = this.newPaymentCard ? TokeniserSite.PlatformAddCard : TokeniserSite.PlatformEditCard;
      const fieldValues = this.getIframeFormValues(this.paymentCardForm.value, true);

      this.paymentCardForm.markAllAsTouched();
      this.tokeniserURL = this.tokeniserService.getTokeniserURL(tokeniserSite, this.currentCardType, fieldValues);
    }
  }

  public removePaymentCard() {
    this.ngrxStore.dispatch(new MemberProfileStore.DestroyProfilePaymentCard(this.pastDataId));
    this.closeForm();
  }

  public onMessage(event: MessageEvent<TokeniserIFrameMessage>) {
    if (event?.data?.allFieldValue === undefined) {
      return;
    }
    const error = event?.data?.tokeniserError;
    const expiryYear = Number.parseInt(event?.data?.allFieldValue?.expiryYear) + 2000;
    const expiryMonth = Number.parseInt(event?.data?.allFieldValue?.expiryMonth);

    if (error) {
      this.tokeniserURL.handleFetchError(error, this.getIframeFormValues(this.paymentCardForm.value));
    }

    this.tokenizerValid = event?.data?.allFieldsValid;
    this.paymentCardForm.patchValue({
      card_holder_name: event?.data?.allFieldValue?.cardHolderNameToken,
      card_number: event?.data?.allFieldValue?.cardNumberToken,
      expiry_date_month: expiryMonth || undefined,
      expiry_date_year: expiryYear || undefined,
    });
  }

  public closeForm() {
    this.closed.emit();
  }

  private getIframeFormValues(card: IMemberProfilePaymentCard, validityCheck = false): IframeFormValues {
    return {
      cardHolderNameToken: card?.card_holder_name,
      cardNumberToken: card?.card_number,
      expiryMonth: card?.expiry_date_month,
      expiryYear: card?.expiry_date_year,
      validityCheck: `${validityCheck}`,
    };
  }

  private setStateValidators(country: ICountry) {
    this.stateRequired = country?.state_or_province_is_required;
    const stateControl = this.paymentCardForm.get('address.state');
    const stateValidators = this.stateRequired
      ? [this.noSpecialCharsValidator, Validators.required]
      : [this.noSpecialCharsValidator];

    stateControl.setValidators(stateValidators);
    stateControl.markAsTouched();
    stateControl.updateValueAndValidity({ emitEvent: false });
  }

  private initEditForm(paymentCardToEdit: IMemberProfilePaymentCard) {
    let country$: Observable<ICountry> = of(null);

    if (paymentCardToEdit.address?.country) {
      country$ = this.countries$.pipe(
        take(1),
        map((countries) => countries.find((country) => country.code === paymentCardToEdit.address.country))
      );
    }

    this.paymentCardForm.controls.card_type.disable();
    // Not visible on edit - when creating a card it gets concatenated to the street1 field
    (<UntypedFormGroup>this.paymentCardForm.controls.address).removeControl('house_number');

    country$.subscribe((country) => {
      this.paymentCardForm.patchValue(paymentCardToEdit);

      if (country) {
        this.paymentCardForm.get('address.country').setValue(country.name);
        this.setStateValidators(country);
      }
    });
  }
}
